import { DashboardVariation } from '@/types/graphql';
import { VisitFilter } from 'gql-types';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import moment, { Moment } from 'moment';
import {
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { z } from 'zod';

import { useCurrentOrganization } from '../../fetching/api/organization';
import { GraphInputHelpers } from '../../fetching/graphql/GraphInputHelpers';
import { SelectedTag, useSelectedTagFilter } from '../../state/tagFilterState';
import { FeatureFlag } from '../../types/featureFlag';
import { atomWithLocalStorage } from '../../utils/browserStorage';
import { useQueryParam } from '../../utils/queryParams';
import { useFeatureFlagEnabled } from '../../utils/useFeatureFlagEnabled';
import { injectTagFilter } from './containers/tagFilterInjection';
import { buildAppliedVisitFilter } from './containers/todaysVisitsFilter';
import { DashboardContainer, RootEntity } from './containers/types';
import {
    getDefaultEnabledContainerKeys,
    useDashboardVariationConfig,
} from './containers/variations/config';
import { runNorthStarPreferenceReset } from './northStarPreferenceReset';

// Run before the storage atom is constructed below so the atom hydrates from
// the post-migration localStorage value on first read.
runNorthStarPreferenceReset();

interface IDashboardPageContext {
    variation: DashboardVariation;
    date: Date;
    dateMoment: Moment;
    dateLabel: string;
    dateInput: string | null;
    availableContainers: DashboardContainer<RootEntity>[];
    enabledContainers: DashboardContainer<RootEntity>[];
    toggleContainerEnabled: (containerKey: string) => void;
    selectedContainer: DashboardContainer<RootEntity> | null;
    setSelectedContainer: (container: DashboardContainer<RootEntity> | null) => void;
    supportsTagFiltering: boolean;
    selectedTags: SelectedTag[];
    setSelectedTags: (tags: SelectedTag[]) => void;
    // VisitFilter applied to dashboard queries; null when no filter is active.
    appliedVisitFilter: VisitFilter | null;
}

const DashboardPageContext = createContext<IDashboardPageContext | null>(null);

// Stores the user's enabled containers for each dashboard variation under a single
// key in local storage. We allow this configuration to persist across logout
// as it contains only UX preferences and no user-revealing data.
// We may end up storing these in the database in the future so that preferences
// are accessible across devices, but local storage gets the job done for now.
const enabledContainersByVariationAtom = atomWithLocalStorage(
    'enabledContainersByVariation',
    {},
    z.record(z.nativeEnum(DashboardVariation), z.array(z.string())),
);

// Derives an atom for a user's enabled containers for a single dashboard variation.
const enabledContainersForVariationAtom = atomFamily((variation: DashboardVariation) =>
    atom(
        (get) =>
            get(enabledContainersByVariationAtom)[variation] ??
            getDefaultEnabledContainerKeys(variation),
        (get, set, value: SetStateAction<string[]>) => {
            const prev = get(enabledContainersByVariationAtom);
            const nextValue =
                typeof value === 'function'
                    ? value(prev[variation] ?? getDefaultEnabledContainerKeys(variation))
                    : value;
            set(enabledContainersByVariationAtom, {
                ...prev,
                [variation]: nextValue,
            });
        },
    ),
);

// Global atom for the selected container. This persists in memory across navigation
// but does NOT persist to localStorage, so it resets on page refresh or new tabs.
const selectedContainerAtom = atom<DashboardContainer<RootEntity> | null>(null);

export const DashboardPageContextProvider = ({ children }: { children: ReactNode }) => {
    // Config from current dashboard variation
    const config = useDashboardVariationConfig();
    const supportsTagFiltering = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_TAG_FILTERS_ON_HOMEPAGE,
    );

    // When tag filtering is disabled, include legacy agency-specific container
    // variants so schedulers can still filter by agency the old way.
    const availableContainers = config.containers;

    // Org ID for tag filter persistence
    const org = useCurrentOrganization();
    const orgId = org?.id ?? '';

    // Tag filter state
    const { selectedTags, setSelectedTags, tagFilter } = useSelectedTagFilter(orgId);

    // Container state - use global atom to persist across navigation
    const [selectedContainer, setSelectedContainer] = useAtom(selectedContainerAtom);
    const [enabledContainerKeys, setEnabledContainerKeys] = useAtom(
        enabledContainersForVariationAtom(config.variation),
    );
    const enabledContainerKeySet = useMemo(
        () => new Set(enabledContainerKeys),
        [enabledContainerKeys],
    );
    const baseEnabledContainers = useMemo(
        () => availableContainers.filter((container) => enabledContainerKeySet.has(container.key)),
        [availableContainers, enabledContainerKeySet],
    );

    const appliedTagFilter = useMemo(
        () => (supportsTagFiltering ? tagFilter : null),
        [supportsTagFiltering, tagFilter],
    );

    // "Today's Visits" filter: combine the optional per-variation filter (e.g.
    // Pinnacle excludes Misc-discipline visits) with the active tag filter.
    // Both are scoped to this org's config, so there's no cross-org leakage.
    const appliedVisitFilter = useMemo<VisitFilter | null>(
        () => buildAppliedVisitFilter(config.todaysVisitsFilter, appliedTagFilter),
        [appliedTagFilter, config.todaysVisitsFilter],
    );

    // Apply tag filter injection when active
    const enabledContainers = useMemo(() => {
        if (!appliedTagFilter) {
            return baseEnabledContainers;
        }
        return baseEnabledContainers.map((container) =>
            injectTagFilter(container, appliedTagFilter),
        );
    }, [baseEnabledContainers, appliedTagFilter]);

    // Ensure the selected container reference stays in sync with the
    // enabledContainers list (which gets new object identities when tag filters
    // change).  We depend only on the container's *key* — not the full object —
    // to avoid a setState→rerender→effect loop.
    const selectedContainerKey = selectedContainer?.key ?? null;
    useEffect(() => {
        if (!selectedContainerKey) {
            return;
        }
        const match = enabledContainers.find((c) => c.key === selectedContainerKey);
        setSelectedContainer(match ?? null);
    }, [enabledContainers, selectedContainerKey, setSelectedContainer]);

    // Container functions
    const toggleContainerEnabled = useCallback(
        (containerKey: string) => {
            setEnabledContainerKeys((prev) => {
                if (prev.includes(containerKey)) {
                    return prev.filter((key) => key !== containerKey);
                } else {
                    return [...prev, containerKey];
                }
            });
        },
        [setEnabledContainerKeys],
    );

    // If a date query parameter is provided, use that date. Otherwise, use
    // today's date.
    //
    // This date never changes automatically as time passes (e.g. when midnight
    // passes and "today" changes), only when the page is first loaded or when
    // the query parameter is explicitly changed.
    const [dateStr] = useQueryParam('date', '');
    const dateMoment = useMemo(() => {
        if (dateStr === '') {
            return moment().startOf('day');
        }
        // Strictly parse the date string, raising an error if the date is invalid.
        // It's okay for this to cause an error page to show, as this is an
        // internal debugging feature.
        return moment(dateStr, 'YYYY-MM-DD', true);
    }, [dateStr]);

    // Formats the current date for display, prefixing with 'Today, ' if the date
    // is today and suffixing with the year if it's not the current year.
    const isToday = dateMoment.isSame(moment(), 'day');
    const isThisYear = dateMoment.isSame(moment(), 'year');
    const dateLabel = `${isToday ? 'Today, ' : ''}${dateMoment.format('dddd, MMMM Do')}${
        isThisYear ? '' : ` ${dateMoment.format('YYYY')}`
    }`;
    const dateInput = GraphInputHelpers.date(dateMoment);

    return (
        <DashboardPageContext.Provider
            value={{
                variation: config.variation,

                // Selected date
                date: dateMoment.toDate(),
                dateMoment,
                dateLabel,
                dateInput,

                // Containers
                availableContainers,
                enabledContainers,
                selectedContainer,
                setSelectedContainer,
                toggleContainerEnabled,

                // Tag filtering
                supportsTagFiltering,
                selectedTags,
                setSelectedTags,
                appliedVisitFilter,
            }}
        >
            {children}
        </DashboardPageContext.Provider>
    );
};

export const useDashboardPageContext = () => {
    const context = useContext(DashboardPageContext);
    if (!context) {
        throw new Error(
            'useDashboardPageContext must be used within a DashboardPageContextProvider',
        );
    }
    return context;
};
